import React, { Component } from 'react'
import { navigate } from '@reach/router'
import { css, StyleSheet } from 'aphrodite'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import BootstrapForm from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Spinner from 'react-bootstrap/Spinner'
import BootstrapTable from 'react-bootstrap/Table'

import { Header, ListFilters, Page } from 'components'
import { ReferralCampaignApi } from 'api'
import { pages, ReferralCampaign, ReferralCampaignCode } from 'types'
import { referralCampaignCreate, referralCampaignEdit } from 'utils/endpoints'
import { isSuperAdmin } from 'utils/permission'
import { matchesListSearch } from 'utils/listFilters'
import { getListFilters, persistListFilters } from 'utils/listViewState'
import {
  buildReferralCampaignCodesUpdatePayload,
  emptyReferralCampaignCode,
  parseOptionalNumber,
  validateReferralCampaignCodes,
  ValidationErrors,
} from './referralCodeModal.helpers'

interface Props {
  path: string
}

interface State {
  codeModalCampaign?: ReferralCampaign
  codeModalCampaignKey?: string
  codeModalCodes: ReferralCampaignCode[]
  codeModalErrors: ValidationErrors
  codeModalLoading: boolean
  codeModalSaving: boolean
  codeModalVisible: boolean
  loading: boolean
  search: string
  statusFilter: string
}

export default class ReferralCampaignsPage extends Component<Props, State> {
  state: State = {
    codeModalCodes: [],
    codeModalErrors: {},
    codeModalLoading: false,
    codeModalSaving: false,
    codeModalVisible: false,
    loading: true,
    ...getListFilters(pages.REFERRAL_CAMPAIGNS_PAGE, {
      search: '',
      statusFilter: 'all',
    }),
  }

  page?: Page
  campaigns: ReferralCampaign[] = []

  async componentDidMount() {
    await this.loadCampaigns()
  }

  componentDidUpdate(_previousProps: Props, previousState: State) {
    const { search, statusFilter } = this.state
    if (previousState.search !== search || previousState.statusFilter !== statusFilter) {
      persistListFilters(pages.REFERRAL_CAMPAIGNS_PAGE, { search, statusFilter })
    }
  }

  loadCampaigns = async () => {
    try {
      this.campaigns = await ReferralCampaignApi.getCampaigns()
      this.setState({ loading: false })
    } catch (e) {
      this.setState({ loading: false })
      this.page?.showNotification({ message: this.getErrorMessage(e), type: 'error' })
    }
  }

  getErrorMessage = (error: any) => {
    return (
      error?.response?.data?.errorDescription ??
      error?.response?.data?.message ??
      error?.message ??
      'Operazione non riuscita.'
    )
  }

  getCampaignStatus = (campaign: ReferralCampaign) => {
    const now = new Date()
    const startsAt = campaign.campaignStartsAt ? new Date(campaign.campaignStartsAt) : undefined
    const endsAt = campaign.campaignEndsAt ? new Date(campaign.campaignEndsAt) : undefined

    if (startsAt && startsAt > now) return 'Programmata'
    if (endsAt && endsAt < now) return 'Terminata'
    if (startsAt || endsAt) return 'Attiva'
    return 'Bozza'
  }

  formatDate = (value?: string) => {
    if (!value) return '-'
    const date = new Date(value)
    if (isNaN(date.getTime())) return value
    return date.toLocaleDateString('it-IT')
  }

  getDuration = (campaign: ReferralCampaign) => {
    const durationDays = campaign.durationDays !== undefined ? `${campaign.durationDays} giorni` : '-'
    const activeHours = campaign.activeHours !== undefined ? `${campaign.activeHours} ore` : '-'
    return `${durationDays} / ${activeHours}`
  }

  getDates = (campaign: ReferralCampaign) => {
    return `${this.formatDate(campaign.campaignStartsAt)} - ${this.formatDate(campaign.campaignEndsAt)}`
  }

  getCodeCountLabel = (campaign: ReferralCampaign) => {
    return `${campaign.enabledCodeCount ?? campaign.codes?.filter((code) => code.enabled !== false).length ?? 0}/${
      campaign.codeCount ?? campaign.codes?.length ?? 0
    }`
  }

  getFilteredCampaigns = () => {
    const { search, statusFilter } = this.state

    return this.campaigns.filter((campaign) => {
      const codeValues = campaign.codes?.map((code) =>
        [code.code, code.label, code.creatorName, code.creatorSlug, code.creatorEmail].join(' ')
      )
      const productValues = campaign.products?.map((product) =>
        [
          product.baseProductRef,
          product.baseProduct?.key,
          product.baseProduct?.title,
          product.baseProduct?.name,
          product.promoProduct?.key,
          product.promoProduct?.title,
          product.promoProduct?.storeProductId,
        ].join(' ')
      )
      const matchesSearch = matchesListSearch(search, [
        campaign.campaignKey,
        campaign.campaignName,
        campaign.description,
        campaign.discountSkuSuffix,
        codeValues,
        productValues,
      ])
      const matchesStatus = statusFilter === 'all' || this.getCampaignStatus(campaign) === statusFilter
      return matchesSearch && matchesStatus
    })
  }

  openCodesModal = async (campaignKey?: string) => {
    if (!campaignKey) return

    this.setState({
      codeModalCampaign: undefined,
      codeModalCampaignKey: campaignKey,
      codeModalCodes: [],
      codeModalErrors: {},
      codeModalLoading: true,
      codeModalSaving: false,
      codeModalVisible: true,
    })

    try {
      const campaign = await ReferralCampaignApi.getCampaign(campaignKey)
      this.setState({
        codeModalCampaign: campaign,
        codeModalCodes: campaign.codes?.length ? campaign.codes : [emptyReferralCampaignCode()],
        codeModalLoading: false,
      })
    } catch (e) {
      this.setState({ codeModalLoading: false, codeModalVisible: false })
      this.page?.showNotification({ message: this.getErrorMessage(e), type: 'error' })
    }
  }

  closeCodesModal = () => {
    if (this.state.codeModalSaving) return

    this.setState({
      codeModalCampaign: undefined,
      codeModalCampaignKey: undefined,
      codeModalCodes: [],
      codeModalErrors: {},
      codeModalLoading: false,
      codeModalSaving: false,
      codeModalVisible: false,
    })
  }

  clearCodeError = (errors: ValidationErrors, fieldKey: string) => {
    if (!errors[fieldKey]) return errors

    const nextErrors = { ...errors }
    delete nextErrors[fieldKey]
    return nextErrors
  }

  setCodeField = (index: number, key: keyof ReferralCampaignCode, value: any) => {
    const fieldKey = `code.${index}.${String(key)}`
    this.setState((state) => {
      const codeModalCodes = [...state.codeModalCodes]
      codeModalCodes[index] = { ...codeModalCodes[index], [key]: value }

      return {
        codeModalCodes,
        codeModalErrors: this.clearCodeError(state.codeModalErrors, fieldKey),
      }
    })
  }

  setCodeNumberField = (index: number, key: keyof ReferralCampaignCode, value: string) => {
    this.setCodeField(index, key, parseOptionalNumber(value))
  }

  addCode = () => {
    this.setState((state) => ({
      codeModalCodes: [...state.codeModalCodes, emptyReferralCampaignCode()],
    }))
  }

  removeCode = (index: number) => {
    const code = this.state.codeModalCodes[index]
    const hasOnlyOneCode = this.state.codeModalCodes.length === 1
    const message = hasOnlyOneCode
      ? 'La campagna deve mantenere almeno un codice. Questo codice verra disabilitato.'
      : 'Il codice verra rimosso dal payload. Se esiste gia su DB, il backend lo disabilitera senza eliminarlo.'

    this.page?.showConfirmDialog('Rimuovere codice?', message, () => {
      this.setState((state) => {
        if (hasOnlyOneCode) {
          const codeModalCodes = [
            code?.code ? { ...state.codeModalCodes[0], enabled: false } : emptyReferralCampaignCode(),
          ]
          return { codeModalCodes }
        }

        return {
          codeModalCodes: state.codeModalCodes.filter((_, i) => i !== index),
        }
      })
    })
  }

  saveCodesModal = async () => {
    const { codeModalCampaign, codeModalCampaignKey, codeModalCodes } = this.state
    if (!codeModalCampaign || !codeModalCampaignKey) return

    const codeModalErrors = validateReferralCampaignCodes(codeModalCodes)
    if (Object.keys(codeModalErrors).length > 0) {
      this.setState({ codeModalErrors })
      this.page?.showNotification({ message: 'Correggi i codici evidenziati prima di salvare.', type: 'error' })
      return
    }

    this.setState({ codeModalSaving: true, codeModalErrors: {} })

    try {
      await ReferralCampaignApi.updateCampaign(
        codeModalCampaignKey,
        buildReferralCampaignCodesUpdatePayload(codeModalCampaign, codeModalCodes)
      )
      this.page?.showNotification({ message: 'Codici sconto aggiornati.', type: 'success' })
      this.setState({ codeModalSaving: false, codeModalVisible: false })
      await this.loadCampaigns()
    } catch (e) {
      this.setState({ codeModalSaving: false })
      this.page?.showNotification({ message: this.getErrorMessage(e), type: 'error' })
    }
  }

  renderNumberValue = (value?: number) => {
    if (typeof value !== 'number' || Number.isNaN(value)) return ''
    return value
  }

  getCodeFieldError = (index: number, key: keyof ReferralCampaignCode) => {
    return this.state.codeModalErrors[`code.${index}.${String(key)}`]
  }

  renderCodeModalRow = (code: ReferralCampaignCode, index: number) => {
    return (
      <div className={css(s.codeCard)} key={`code-modal-${index}`}>
        <div className={css(s.codeCardHeader)}>
          <b>Codice {index + 1}</b>
          <Button variant="outline-danger" size="sm" onClick={() => this.removeCode(index)}>
            Rimuovi
          </Button>
        </div>
        <div className={css(s.codeGrid)}>
          <BootstrapForm.Group className={css(s.field)}>
            <BootstrapForm.Label>Code *</BootstrapForm.Label>
            <BootstrapForm.Control
              isInvalid={Boolean(this.getCodeFieldError(index, 'code'))}
              value={code.code ?? ''}
              onChange={(event: any) => this.setCodeField(index, 'code', event.currentTarget.value)}
            />
            <BootstrapForm.Control.Feedback type="invalid">
              {this.getCodeFieldError(index, 'code')}
            </BootstrapForm.Control.Feedback>
          </BootstrapForm.Group>
          <BootstrapForm.Group className={css(s.field)}>
            <BootstrapForm.Label>Label</BootstrapForm.Label>
            <BootstrapForm.Control
              value={code.label ?? ''}
              onChange={(event: any) => this.setCodeField(index, 'label', event.currentTarget.value)}
            />
          </BootstrapForm.Group>
          <BootstrapForm.Group className={css(s.field)}>
            <BootstrapForm.Label>Creator name</BootstrapForm.Label>
            <BootstrapForm.Control
              value={code.creatorName ?? ''}
              onChange={(event: any) => this.setCodeField(index, 'creatorName', event.currentTarget.value)}
            />
          </BootstrapForm.Group>
          <BootstrapForm.Group className={css(s.field)}>
            <BootstrapForm.Label>Creator slug</BootstrapForm.Label>
            <BootstrapForm.Control
              value={code.creatorSlug ?? ''}
              onChange={(event: any) => this.setCodeField(index, 'creatorSlug', event.currentTarget.value)}
            />
          </BootstrapForm.Group>
          <BootstrapForm.Group className={css(s.field)}>
            <BootstrapForm.Label>Creator email</BootstrapForm.Label>
            <BootstrapForm.Control
              type="email"
              value={code.creatorEmail ?? ''}
              onChange={(event: any) => this.setCodeField(index, 'creatorEmail', event.currentTarget.value)}
            />
          </BootstrapForm.Group>
          <BootstrapForm.Group className={css(s.field)}>
            <BootstrapForm.Label>Max redemptions</BootstrapForm.Label>
            <BootstrapForm.Control
              isInvalid={Boolean(this.getCodeFieldError(index, 'maxRedemptions'))}
              type="number"
              value={this.renderNumberValue(code.maxRedemptions)}
              onChange={(event: any) => this.setCodeNumberField(index, 'maxRedemptions', event.currentTarget.value)}
            />
            <BootstrapForm.Control.Feedback type="invalid">
              {this.getCodeFieldError(index, 'maxRedemptions')}
            </BootstrapForm.Control.Feedback>
          </BootstrapForm.Group>
          <BootstrapForm.Group className={css(s.field)}>
            <BootstrapForm.Label>Commissione %</BootstrapForm.Label>
            <BootstrapForm.Control
              isInvalid={Boolean(this.getCodeFieldError(index, 'commissionPercent'))}
              type="number"
              value={this.renderNumberValue(code.commissionPercent)}
              onChange={(event: any) => this.setCodeNumberField(index, 'commissionPercent', event.currentTarget.value)}
            />
            <BootstrapForm.Control.Feedback type="invalid">
              {this.getCodeFieldError(index, 'commissionPercent')}
            </BootstrapForm.Control.Feedback>
          </BootstrapForm.Group>
          <BootstrapForm.Group className={css(s.checkboxField)}>
            <BootstrapForm.Check
              checked={code.enabled !== false}
              label="Enabled"
              type="checkbox"
              onChange={(event: any) => this.setCodeField(index, 'enabled', event.currentTarget.checked)}
            />
          </BootstrapForm.Group>
        </div>
      </div>
    )
  }

  renderCodesModal = () => {
    const { codeModalCampaign, codeModalCodes, codeModalLoading, codeModalSaving, codeModalVisible } = this.state

    return (
      <Modal show={codeModalVisible} onHide={this.closeCodesModal} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            Codici sconto {codeModalCampaign?.campaignName ?? codeModalCampaign?.campaignKey ?? ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {codeModalLoading && <Spinner animation="border" />}
          {!codeModalLoading && (
            <>
              <Alert variant="info">
                Qui modifichi solo i codici della campagna. Prodotti, date e percentuali campagna restano invariati.
              </Alert>
              {codeModalCodes.map(this.renderCodeModalRow)}
              <Button variant="outline-primary" onClick={this.addCode}>
                Aggiungi codice
              </Button>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={codeModalSaving} onClick={this.closeCodesModal}>
            Annulla
          </Button>
          <Button variant="primary" disabled={codeModalLoading || codeModalSaving} onClick={this.saveCodesModal}>
            {codeModalSaving ? 'Salvataggio...' : 'Salva codici'}
          </Button>
        </Modal.Footer>
      </Modal>
    )
  }

  renderCampaignsTable = (campaigns: ReferralCampaign[]) => {
    if (campaigns.length === 0) return <h3 style={{ textAlign: 'center' }}>Nessun risultato trovato</h3>

    return (
      <BootstrapTable bordered responsive>
        <thead className="thead-light">
          <tr>
            <th>Key</th>
            <th>Nome</th>
            <th>Stato</th>
            <th>Sconto</th>
            <th>Commissione</th>
            <th>Durata/Attiva</th>
            <th>Date</th>
            <th>Codici</th>
            <th>Prodotti</th>
            <th>Operazioni</th>
          </tr>
        </thead>
        <tbody>
          {campaigns.map((campaign, index) => (
            <tr className={index % 2 === 1 ? css(s.darkRow) : undefined} key={campaign.campaignKey}>
              <td>{campaign.campaignKey ?? '-'}</td>
              <td>{campaign.campaignName ?? '-'}</td>
              <td>{this.getCampaignStatus(campaign)}</td>
              <td>{campaign.discountPercent !== undefined ? `${campaign.discountPercent}%` : '-'}</td>
              <td>{campaign.commissionPercent !== undefined ? `${campaign.commissionPercent}%` : '-'}</td>
              <td>{this.getDuration(campaign)}</td>
              <td>{this.getDates(campaign)}</td>
              <td>{this.getCodeCountLabel(campaign)}</td>
              <td>{campaign.productMappingCount ?? campaign.products?.length ?? 0}</td>
              <td className={css(s.actionsCell)}>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => navigate(referralCampaignEdit + campaign.campaignKey)}
                >
                  Modifica
                </Button>
                <Button variant="outline-primary" size="sm" onClick={() => this.openCodesModal(campaign.campaignKey)}>
                  Codici
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </BootstrapTable>
    )
  }

  render() {
    const { loading } = this.state
    const filteredCampaigns = this.getFilteredCampaigns()

    if (!isSuperAdmin())
      return <h1 style={{ textAlign: 'center', marginTop: 50 }}>Non puoi accedere a questa pagina</h1>

    return (
      <Page ref={(r) => (this.page = r!)} page={pages.REFERRAL_CAMPAIGNS_PAGE}>
        {loading && <Spinner animation="border" />}
        {!loading && (
          <>
            <Header title={'Referral Creator'} buttons={[{ title: 'Crea campagna', href: referralCampaignCreate }]} />
            {this.renderCodesModal()}
            <ListFilters
              searchValue={this.state.search}
              searchPlaceholder="Key, nome, codice o prodotto"
              onSearchChange={(search) => this.setState({ search })}
              filters={[
                {
                  key: 'statusFilter',
                  label: 'Stato',
                  value: this.state.statusFilter,
                  options: [
                    { label: 'Tutti', value: 'all' },
                    { label: 'Bozza', value: 'Bozza' },
                    { label: 'Programmate', value: 'Programmata' },
                    { label: 'Attive', value: 'Attiva' },
                    { label: 'Terminate', value: 'Terminata' },
                  ],
                },
              ]}
              onFilterChange={(_, statusFilter) => this.setState({ statusFilter })}
              onReset={() => this.setState({ search: '', statusFilter: 'all' })}
              resultCount={filteredCampaigns.length}
              totalCount={this.campaigns.length}
            />
            {this.renderCampaignsTable(filteredCampaigns)}
          </>
        )}
      </Page>
    )
  }
}

const s = StyleSheet.create({
  actionsCell: {
    display: 'flex',
    gap: 8,
    justifyContent: 'center',
  },
  checkboxField: {
    alignItems: 'center',
    display: 'flex',
    marginTop: 36,
  },
  codeCard: {
    border: '1px solid var(--color-rule)',
    borderRadius: 4,
    marginBottom: 12,
    padding: 12,
  },
  codeCardHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  codeGrid: {
    display: 'grid',
    gridColumnGap: 12,
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  },
  darkRow: {
    backgroundColor: 'var(--color-paper-3)',
  },
  field: {
    marginTop: 8,
  },
})
